import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { Product } from '@/lib/types';
import { searchProducts } from '@/lib/smartSearch';

const PAGE_SIZE = 24;

interface CatalogProductRow {
  id: string;
  name: string;
  sku: string | null;
  barcode: string | null;
  brand: string | null;
  description: string | null;
  category_id: string | null;
  category_name: string | null;
  sale_price: number;
  tax_percentage: number | null;
  stock: number | null;
  min_stock: number | null;
  image_url: string | null;
  show_in_catalog: boolean;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

// Loads every catalog-visible product once, then filters/ranks in memory so the
// search can be accent-insensitive, multi-word and relevance-ordered.
export function useCatalogProducts(search: string, categoryId: string | null) {
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // El catálogo público usa una función que solo expone campos públicos
      // (nunca el costo ni el precio mayorista).
      const { data, error: e } = await supabase.rpc('catalog_products_v2');
      if (e) throw e;

      const rows: Product[] = ((data ?? []) as CatalogProductRow[]).map((r) => ({
        id: r.id,
        name: r.name,
        sku: r.sku,
        barcode: r.barcode,
        brand: r.brand,
        description: r.description,
        category_id: r.category_id,
        sale_price: Number(r.sale_price ?? 0),
        tax_percentage: r.tax_percentage,
        stock: Number(r.stock ?? 0),
        min_stock: r.min_stock,
        image_url: r.image_url,
        show_in_catalog: r.show_in_catalog,
        active: r.active,
        wholesale_enabled: false,
        purchase_price: null,
        wholesale_price: null,
        category: r.category_id ? { id: r.category_id, name: r.category_name ?? '' } : null,
        created_at: r.created_at,
        updated_at: r.updated_at,
      }));
      const stockMap = new Map<string, number>();

      if (rows.length > 0) {
        const ids = rows.map((r) => r.id);
        const { data: inv } = await supabase
          .from('branch_inventory')
          .select('product_id, stock')
          .in('product_id', ids);
        for (const r of (inv ?? []) as { product_id: string; stock: number }[]) {
          stockMap.set(r.product_id, (stockMap.get(r.product_id) ?? 0) + Number(r.stock ?? 0));
        }
      }

      setAllProducts(rows.map((p) => ({ ...p, stock: stockMap.get(p.id) ?? 0 })));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los productos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    let list = allProducts;
    if (categoryId) list = list.filter((p) => p.category_id === categoryId);
    return searchProducts(list, search);
  }, [allProducts, search, categoryId]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, categoryId]);

  const products = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const loadMore = useCallback(() => {
    setVisibleCount((count) => count + PAGE_SIZE);
  }, []);

  return {
    products,
    total: filtered.length,
    loading,
    error,
    hasMore,
    loadMore,
    reload: load,
  };
}